import { HTMLProps, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ILocalAudioTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack
} from 'agora-rtc-sdk-ng';
import cx from 'classnames';

import { FacingMode } from 'types';
import { RTCService } from 'services';
import { AudioWave, VideoPlayer } from 'components';
import { MicrophoneOffIcon } from 'components/Icon';

import Image from './Speaker.Image';

import styles from './Speaker.module.scss';

export type SpeakerProps = Pick<HTMLProps<HTMLDivElement>, 'className' | 'onClick'> & {
  seatIndex?: number;
  name?: string;
  image?: string;
  audioTrack?: ILocalAudioTrack | IRemoteAudioTrack;
  videoTrack?: ILocalVideoTrack | IRemoteVideoTrack;
  fit?: 'cover' | 'contain' | 'fill';
  active?: boolean;
  squared?: boolean;
  isActiveSpeaker?: boolean;
  audioMuted?: boolean;
  videoMuted?: boolean;
  type?: 'local' | 'remote';
  facingMode?: FacingMode;
  isGridItem?: boolean;
  withAudioIcon?: boolean;
};

const Speaker = ({
  seatIndex,
  className,
  name,
  image,
  audioTrack,
  videoTrack,
  fit = 'cover',
  active = false,
  squared = false,
  isActiveSpeaker = false,
  audioMuted = false,
  videoMuted = false,
  type = 'remote',
  facingMode = FacingMode.FRONT,
  isGridItem = false,
  withAudioIcon = false,
  onClick
}: SpeakerProps) => {
  const videoContainer = useRef<HTMLDivElement>(null);
  const prevVideoTrack = useRef(videoTrack);

  const mirror = useMemo<boolean>(() => type === 'local' && facingMode === FacingMode.FRONT, [type, facingMode]);

  useEffect(() => {
    if (prevVideoTrack.current !== videoTrack && videoTrack) {
      prevVideoTrack.current && RTCService.stopVideo(prevVideoTrack.current);
      prevVideoTrack.current = videoTrack;
    }
  }, [videoTrack]);

  useEffect(() => {
    if (videoTrack && videoContainer.current && !videoMuted) {
      RTCService.playVideo(videoTrack, videoContainer.current, { fit, mirror });
    } else if (videoMuted) {
      videoTrack && RTCService.stopVideo(videoTrack);
    }
  }, [videoTrack, fit, mirror, videoMuted]);

  const handleOnClickVideoPlayer = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isActiveSpeaker) {
        event.stopPropagation();
      }
    },
    [isActiveSpeaker]
  );

  const renderAudio = () => {
    if (!withAudioIcon) return null;

    return (
      <div className={cx(styles.audio, { [styles.muted]: audioMuted })}>
        {audioMuted ? <MicrophoneOffIcon /> : <AudioWave track={audioTrack as IMicrophoneAudioTrack} />}
      </div>
    );
  };

  const renderNameAndMicrophone = () => {
    if (isGridItem) {
      return (
        <>
          <div className={styles.nameContainer}>
            {renderAudio()}
            <span className={styles.name}>{name}</span>
          </div>
        </>
      );
    }

    return renderAudio();
  };

  return (
    <div
      aria-hidden="true"
      className={cx(className, styles.container, {
        [styles.active]: active,
        [styles.squared]: squared,
        [styles.isGridItem]: isGridItem
      })}
      onClick={onClick}
    >
      <Image className={styles.image} name={name} image={image} seatIndex={seatIndex} />
      <VideoPlayer
        ref={videoContainer}
        className={cx(styles.video, {
          [styles.main]: !isGridItem && videoTrack && isActiveSpeaker && !videoMuted
        })}
        onClick={handleOnClickVideoPlayer}
      />

      {renderNameAndMicrophone()}
    </div>
  );
};

const MemoizedSpeaker = memo(Speaker);

export default MemoizedSpeaker;
